import React, { useState, useMemo } from "react";
import styled, { keyframes } from "styled-components";
import { IoClose } from "react-icons/io5";

// Sidebar
const SidebarContainer = styled.div<{ closing?: boolean }>`
  width: 280px;
  border-radius: 2px;
  height: fit-content;
  position : absolute;
  right : 0;
  top : 35px;
  background: #f9fcf7ff;
  border: 1px solid #ddd;
  padding: 1rem;
  box-shadow: -2px 0 6px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;

  h3 {
    font-size: 1rem;
    font-weight: 600;
    color: #333;
  }

  svg {
    font-size: 1.3rem;
    cursor: pointer;
    color: #555;
    transition: 0.2s ease;

    &:hover {
      color: #28a745;
      transform: scale(1.1);
    }
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.3rem 0.45rem;
  border-radius: 2px;
  border: 1px solid #ccc;
  margin-bottom: .8rem;
  outline: none;
  font-size: 0.9rem;

  &:focus {
    border-color: #28a745;
  }
`;

const Section = styled.div`
  margin-bottom: .9rem;
`;

const SectionTitle = styled.div`
  font-weight: 400;
  margin-bottom: 0.5rem;
  color: gray;
  font-size: .875rem;
`;

const Option = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.3rem;
  font-size: 0.9rem;
  color: #333;
  cursor: pointer;

  input {
    cursor: pointer;
  }
`;

interface FilterExpensePopupProps {
  data: any[];
  onFilterChange: (filters: { statuses: string[]; employees: string[] }) => void;
  onClose: () => void;
}

const FilterExpensePopup: React.FC<FilterExpensePopupProps> = ({
  data,
  onFilterChange,
  onClose,
}) => {
  const [closing, setClosing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);

  const statuses = useMemo(
    () => Array.from(new Set(data.map((item) => item.status))),
    [data]
  );
  const employees = useMemo(
    () => Array.from(new Set(data.map((item) => item.employee?.name))),
    [data]
  );

  const filteredStatuses = statuses.filter((s) =>
    s.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredEmployees = employees.filter((e) =>
    e.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleStatusChange = (status: string) => {
    const updated = selectedStatuses.includes(status)
      ? selectedStatuses.filter((s) => s !== status)
      : [...selectedStatuses, status];
    setSelectedStatuses(updated);
    onFilterChange({ statuses: updated, employees: selectedEmployees });
  };

  const handleEmployeeChange = (emp: string) => {
    const updated = selectedEmployees.includes(emp)
      ? selectedEmployees.filter((e) => e !== emp)
      : [...selectedEmployees, emp];
    setSelectedEmployees(updated);
    onFilterChange({ statuses: selectedStatuses, employees: updated });
  };

  const handleClose = () => {
    setClosing(true);
    setTimeout(onClose, 250); 
  };

  return (
      <SidebarContainer
        closing={closing}
        onClick={(e) => e.stopPropagation()}
      >
        <Header>
          <h3>Filter</h3>
          <IoClose onClick={handleClose} />
        </Header>

        <SearchInput
          type="text"
          placeholder="Search status or employee"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <Section>
          <SectionTitle>Status</SectionTitle>
          {filteredStatuses.map((status) => (
            <Option key={status}>
              <input
                type="checkbox"
                checked={selectedStatuses.includes(status)}
                onChange={() => handleStatusChange(status)}
              />
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Option>
          ))}
        </Section>

        <Section>
          <SectionTitle>Employees</SectionTitle>
          {filteredEmployees.map((emp) => (
            <Option key={emp}>
              <input
                type="checkbox"
                checked={selectedEmployees.includes(emp)}
                onChange={() => handleEmployeeChange(emp)}
              />
              {emp}
            </Option>
          ))}
        </Section>
      </SidebarContainer>
  );
};

export default FilterExpensePopup;
